import { reaction } from "mobx";
import moment from "moment";
import { AttractorWithStatus } from "../types";

/** The current route relative to the router's basename (the <base href>, when one is set). */
export const currentAppPath = () => {
  const base =
    document.querySelector("base")?.getAttribute("href")?.replace(/\/$/, "") ??
    "";
  const { pathname, search } = window.location;
  const path =
    base && pathname.startsWith(base)
      ? pathname.slice(base.length) || "/"
      : pathname;
  return path + search;
};

/**
 * Where /select-venue sends the user once a venue is chosen: the page runAuthHandler sent them
 * from, or home. Auth screens are refused, because a stale redirect can fire while one of them
 * is showing.
 */
export const returnPathFrom = (state: unknown): string => {
  const returnTo = (state as { returnTo?: unknown } | null)?.returnTo;
  if (
    typeof returnTo === "string" &&
    returnTo.startsWith("/") &&
    !returnTo.startsWith("//") &&
    !/^\/(login|select-venue)(\/|\?|$)/.test(returnTo)
  ) {
    return returnTo;
  }
  return "/home";
};

export const runAuthHandler = async ({
  userStore,
  navigate,
}: {
  userStore: any;
  navigate: any;
}) => {
  reaction(
    () => [userStore.isLoggedIn, userStore.selectedVenue],
    (values) => {
      const isLoggedIn = values[0];
      const selectedVenue = values[1];
      if (!isLoggedIn) {
        navigate("/login");
        return;
      }

      if (!selectedVenue) {
        // Carry the requested page along, so choosing a venue lands the user back on it.
        navigate("/select-venue", { state: { returnTo: currentAppPath() } });
        return;
      }
    },
    {
      fireImmediately: true,
    }
  );
};
export const getDisplayStatus = (
  attractor: any,
  currentTime: number
): AttractorWithStatus => {
  let displayStatus: "SHOWING" | "ENDED" | "NOT_STARTED" | undefined =
    undefined;

  let dayDifference: number = 0;

  if (
    attractor.startDateEpoch === undefined ||
    attractor.endDateEpoch === undefined
  ) {
    return { ...attractor, displayStatus, dayDifference: 0 };
  }

  if (
    currentTime >= attractor.startDateEpoch &&
    currentTime <= attractor.endDateEpoch
  ) {
    dayDifference = moment(attractor.endDateEpoch).diff(
      moment(currentTime),
      "days"
    );
    displayStatus = "SHOWING";
  } else if (currentTime > attractor.endDateEpoch) {
    dayDifference = moment(currentTime).diff(
      moment(attractor.endDateEpoch),
      "days"
    );
    displayStatus = "ENDED";
  } else if (currentTime < attractor.startDateEpoch) {
    dayDifference = moment(attractor.startDateEpoch).diff(
      moment(currentTime),
      "days"
    );
    displayStatus = "NOT_STARTED";
  }

  return { ...attractor, displayStatus, dayDifference };
};

export const transformAttractorData = ({ data, setCurrentAttractors }: any) => {
  if (data.length === 0) {
    return data;
  }

  const currentDayOfWeek = moment().format("ddd").toUpperCase();
  const currentTime = moment().valueOf();

  const currentAttractors = data.filter(
    (banner: {
      dayVisibility: string | string[] | undefined;
      startDateEpoch: number | undefined;
      endDateEpoch: number | undefined;
    }) => {
      return (
        banner?.dayVisibility !== undefined &&
        banner?.startDateEpoch !== undefined &&
        banner?.endDateEpoch !== undefined &&
        banner?.dayVisibility?.includes(currentDayOfWeek) &&
        currentTime >= banner?.startDateEpoch &&
        currentTime <= banner?.endDateEpoch
      );
    }
  );

  setCurrentAttractors(currentAttractors);
};
